import { z } from 'zod';

/*
 * Every list endpoint paginates (CLAUDE.md §5), so every list endpoint takes
 * these and returns that shape.
 *
 * Stated once, here, because the alternative is each route inventing its own
 * page-size cap — and the one that forgets is the one somebody fetches with
 * `?pageSize=100000` on the day the client list is long enough to matter.
 */

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

export const listQuerySchema = z.object({
  // `coerce`, because a query string is always text — `?page=2` arrives as "2".
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  search: z.string().trim().max(200).optional(),
});

export type ListQuery = z.infer<typeof listQuerySchema>;

export interface ListResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  /** The number of rows matching the filter, not the number on this page. */
  total: number;
}

/*
 * ---------------------------------------------------------------------------
 * Sorting — owner request, 2026-08-10.
 *
 * **No list in the admin had a sort control. Not one.** Each screen's order was
 * fixed in its service when it was built, which was fine until somebody wanted
 * a different one and there was nowhere to ask for it.
 *
 * **Named orders rather than a column and a direction**, and that is the whole
 * design decision here. Two reasons, and the second is the one that settles it:
 *
 *   1. A closed set of names maps to a known `ORDER BY` on the server. A free
 *      column name arriving from a query string is an injection surface and a
 *      list of columns to keep in sync with every screen.
 *   2. **`DataTable` stops being a table below 768px and becomes cards** (P6),
 *      and a card has no column headers. Clickable headers would mean no
 *      sorting at all on a phone, which is the device the office actually uses.
 *      A control above the list works on both layouts.
 *
 * "Newest" means different columns on different screens — a job's scheduled
 * date, an invoice's created date, a conversation's last message. The label is
 * the same because the question is the same.
 * ---------------------------------------------------------------------------
 */

/** The two orders every record list offers. Newest is the default everywhere. */
export const RECORD_SORTS = ['newest', 'oldest'] as const;
export type RecordSort = (typeof RECORD_SORTS)[number];

/**
 * Lists of people, which get two more.
 *
 * **Clients and employees default to A–Z, not newest** — owner decision,
 * 2026-08-10. You open those screens to find one particular person, and
 * newest-first turns a lookup into a scroll.
 */
export const PEOPLE_SORTS = ['name', 'name_desc', 'newest', 'oldest'] as const;
export type PeopleSort = (typeof PEOPLE_SORTS)[number];

export const SORT_LABELS: Record<RecordSort | PeopleSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  name: 'A–Z',
  name_desc: 'Z–A',
};

/**
 * A sort parameter with a default.
 *
 * **`.catch` as well as `.default`**, so an unknown value falls back instead of
 * failing the request. A bookmarked URL outliving a rename should show the list
 * in the ordinary order, not a validation error where a list used to be.
 */
export function sortParam<const T extends readonly [string, ...string[]]>(
  values: T,
  fallback: T[number],
) {
  return z.enum(values).default(fallback).catch(fallback);
}

/** The three ways a list can be filtered by `is_active` everywhere in the admin. */
export const activityFilterSchema = z.enum(['active', 'inactive', 'all']).default('active');
export type ActivityFilter = z.infer<typeof activityFilterSchema>;

export const ACTIVITY_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
] as const;
